import React, {useState, useEffect, useMemo, useCallback} from "react";
import {required} from "src/core/common/validations";
import {Field} from "react-final-form";
import styled from "styled-components";
import {PrimaryLoadingButton} from "src/core/common/components/elements/button/PrimaryButton";
import Error from "src/core/common/components/elements/Error";
import {useDataSource} from "src/core/data-sources/decorators/withData";
import StrongholdPaymentSourcesDataSource from "src/core/payments/stronghold/strongholdPaymentSourcesDataSource";
import {Trash2} from "styled-icons/feather/Trash2";
import Loader from "src/core/common/components/elements/Loader";
import PaymentSource from "src/core/payments/PaymentSource";
import get from "lodash/get";
import {useForm} from "src/core/common/components/FormContext";

function UserProfileStrongholdAccountSelectionField({
  paymentOption,
  form,
  order,
  readOnly,
  isMyAccount,
  markAsDefault,
  disabled,
}) {
  const _readOnly = readOnly || order;
  const [loading, setLoading] = useState(false);
  const {
    data: paymentSources,
    meta,
    refresh,
  } = useDataSource(
    {},
    {
      dataSource: paymentSourcesDataSource,
    }
  );
  const pSourceMeta = meta;

  const initialPaymentSource = useMemo(
    () =>
      order
        ? order.getPaymentSpecification().getPaymentInfo()
        : Array.isArray(paymentSources) &&
          (paymentSources.find(pSource => pSource.isDefault) || paymentSources[0]),
    [order, paymentSources]
  );

  const {requestDisabled} = useForm();

  const startAsyncRequest = useCallback(() => {
    requestDisabled(true);
    setLoading(true);
  }, [setLoading, requestDisabled]);

  const finishAsyncRequest = useCallback(() => {
    setLoading(false);
    requestDisabled(false);
  }, [setLoading, requestDisabled]);

  useEffect(() => {
    if (!pSourceMeta.loading) finishAsyncRequest();
  }, [pSourceMeta.loading, finishAsyncRequest]);

  const paymentDate =
    order && order.isPaid() ? order.getPaymentSpecification().getPaymentDate() : null;

  useEffect(() => {
    if (!initialPaymentSource) form.mutators.setValue("payment.source", null);

    const currentPaymentSourceId = get(form.getState(), "values.payment.source.id");
    const inList =
      paymentSources &&
      paymentSources.find(pSource => pSource.id === currentPaymentSourceId);
    if (!inList && !_readOnly && paymentSources && paymentSources.length > 0) {
      form.mutators.setValue("payment.source", initialPaymentSource);
    }
    // eslint-disable-next-line
  }, [paymentSources, form.mutators, _readOnly, initialPaymentSource]);

  useEffect(() => {
    return paymentOption.subscribe(({event, data}) => {
      const refreshEvents = [
        "payment-source-marked-as-default",
        "link-account-success",
        "removed-account-success",
      ];
      if (event === "link-account-success" && !isMyAccount) {
        form.mutators.setValue("payment.source", data);
        finishAsyncRequest();
      }
      if (refreshEvents.indexOf(event) > -1) refresh();
      if (event === "link-account-fail") {
        finishAsyncRequest();
      }
    });
  }, [
    refresh,
    paymentOption,
    form.mutators,
    isMyAccount,
    finishAsyncRequest,
    requestDisabled,
  ]);

  async function linkAccount() {
    startAsyncRequest();
    await paymentOption.linkAccount();
  }

  async function removeAccount(paymentSource) {
    if (loading) return;
    startAsyncRequest();
    await paymentOption.removeAccount(paymentSource);
  }

  async function onChange(paymentSource) {
    if (loading || !markAsDefault) return;
    startAsyncRequest();
    await paymentOption.markAsDefaultPaymentMethod(paymentSource);
  }

  const hasAccounts = paymentSources && paymentSources.length > 0;

  const showPaymentSourcesList =
    !pSourceMeta.loading || (paymentSources && paymentSources.length > 0);

  const pSources = order
    ? [order.getPaymentSpecification().getPaymentInfo()]
    : paymentSources;

  const _loading = pSourceMeta.loading || loading;

  const LinkAccountButtonComponent = isMyAccount
    ? LinkAccountButton
    : hasAccounts
    ? LinkAccountText
    : LinkAccountButton;
  return (
    <Container hasAccounts={hasAccounts}>
      {!showPaymentSourcesList && <Loader />}
      <Field
        name={"payment.source"}
        initialValue={initialPaymentSource}
        validate={required}
        render={({input, meta}) => (
          <div className={meta.error ? "invalid" : ""}>
            {showPaymentSourcesList && (
              <>
                {(!pSources || pSources.length === 0) && <>Add a new account</>}
                {pSources && pSources.length > 0 && (
                  <PaymentSourcesList readOnly={_readOnly}>
                    {pSources.map((pSource, index) => {
                      return (
                        <PaymentSourceLine key={index}>
                          <PaymentSource
                            readOnly={readOnly}
                            institution={pSource.institution}
                            institutionName={pSource.institutionName}
                            label={pSource.label}
                            paymentDate={paymentDate}
                            onClick={() => {
                              onChange(pSource);
                              !readOnly && !disabled && input.onChange(pSource);
                            }}
                            checked={input.value && input.value.id === pSource.id}
                            isDefault={isMyAccount && pSource.isDefault}
                            dataCy={`paymentSource-${index}`}
                          />
                          {!_readOnly && (
                            <CloseIcon
                              loading={_loading || disabled}
                              onClick={!disabled && (() => removeAccount(pSource))}
                              data-cy="stronghold-removeAccount"
                            />
                          )}
                        </PaymentSourceLine>
                      );
                    })}
                  </PaymentSourcesList>
                )}
                {(!_readOnly || isMyAccount) && (
                  <LinkAccountButtonComponent
                    loading={_loading}
                    disabled={disabled}
                    onClick={linkAccount}
                    type={"button"}
                    data-cy="linkAccount"
                    label={hasAccounts ? "Link New Account" : "Link Account"}
                  />
                )}
                {meta.touched && <Error>{meta.error}</Error>}
              </>
            )}
          </div>
        )}
      />
    </Container>
  );
}

const paymentSourcesDataSource = new StrongholdPaymentSourcesDataSource();
const Container = styled.div`
  padding: ${({theme}) => theme.v2.spacing(4)} 0 0 0;

  ${({hasAccounts, theme}) =>
    !hasAccounts
      ? `
    width: auto !important;
    border: 1px solid #EBEBEB;
    border-radius: 10px;
    padding: ${theme.v2.spacing(8)};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    > button {
      max-width: 200px;
    }
  `
      : ""};
`;
const LinkAccountButton = styled(PrimaryLoadingButton)`
  margin-top: ${({theme}) => theme.v2.spacing(2)};
  background-color: #ffffff;
  color: ${({theme}) => theme.v2.color.primary};
  border: 1px solid ${({theme}) => theme.v2.color.primary};
  > *:first-child {
    margin-right: ${({theme}) => theme.v2.spacing(2)};
  }
  &:hover {
    opacity: 0.6;
    color: ${({theme}) => theme.v2.color.primary};
  }
`;
const LinkAccountText = styled(PrimaryLoadingButton)`
  border: none;
  background-color: #ffffff;
  color: #000000;
  width: auto;
  float: right;
  &:hover {
    opacity: 0.6;
    color: #000000;
  }
`;
const PaymentSourcesList = styled.ul`
  padding: 0 ${({readOnly, theme}) => (!readOnly ? theme.v2.spacing(4) : 0)};
  list-style-type: none;
  margin: 0;
`;
const PaymentSourceLine = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({theme}) => theme.v2.spacing(4)};
`;
const CloseIcon = styled(Trash2)`
  cursor: ${({loading}) => (loading ? "not-allowed" : "pointer")};
  height: 22px;
  width: 22px;
  margin-left: ${({theme}) => theme.v2.spacing(2)};
`;

export default UserProfileStrongholdAccountSelectionField;
